const BASE = import.meta.env.VITE_API_BASE ?? "/api";

export class ApiError extends Error {
  constructor(public status: number, public payload: unknown, message?: string) {
    super(message ?? `API error ${status}`);
  }
}

async function request<T>(path: string, init: RequestInit = {}, timeoutMs = 15_000): Promise<T> {
  const attempt = () => fetch(`${BASE}${path}`, {
    credentials: "include",
    // Nenhuma request fica pendurada pra sempre: conexão perdida vira erro (default
    // 15s). Requests longas (ex.: turno do Playground) passam um timeoutMs maior.
    signal: AbortSignal.timeout(timeoutMs),
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });
  let resp: Response;
  try {
    resp = await attempt();
  } catch (e) {
    // Conexão keep-alive morta (Swarm/NAT derruba TCP ocioso em silêncio) faz o
    // fetch pendurar até o timeout sem a request chegar no servidor. GET é
    // idempotente: TimeoutError ou queda de rede (TypeError) ganha UMA nova
    // tentativa em conexão nova. Sem isso, o me() do beforeLoad derruba o app
    // inteiro — os useQuery têm retry do react-query, o route-load não tinha.
    const isGet = !init.method || init.method === "GET";
    const transient = (e instanceof DOMException && e.name === "TimeoutError") || e instanceof TypeError;
    if (!isGet || !transient) throw e;
    resp = await attempt();
  }
  if (!resp.ok) {
    let body: unknown = null;
    try { body = await resp.json(); } catch {}
    throw new ApiError(resp.status, body);
  }
  if (resp.status === 204) return undefined as T;
  return resp.json();
}

export const api = {
  get: <T>(path: string, timeoutMs?: number) => request<T>(path, {}, timeoutMs),
  post: <T>(path: string, body: unknown, timeoutMs?: number) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }, timeoutMs),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "DELETE", body: body ? JSON.stringify(body) : undefined }),
};
